import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button, ColorSwatchList, Modal, ModalHeader, ModalFooter, ModalContent } from '@sainsburys-tech/bolt'

import { to } from '../../../../utils'
import { groupByTypeValue } from '../../AttributeSelectorHelpers'

import config from './config/config'

import { updateSwatchModalOpen } from '../../../../actionCreators/productStore/productStoreActionCreators'

import * as Styled from './ColourAndSwatchPicker.styles'

const MAX_VARIATIONS = 7

const Header = ({ colorName, fabricName }) => {
  const headerLabel = fabricName ? `${fabricName} - ${to.titleCase(colorName)}` : `Colour: ${to.titleCase(colorName)}`
  return (
    <Styled.Header>
      <strong>{headerLabel}</strong>
    </Styled.Header>
  )
}

const buildSelector = (combinationsGroupedByType, uniqColors, callback) => index => {
  const exactChoices = combinationsGroupedByType[uniqColors[index]].filter(combination => !combination.otherCombination)
  if (exactChoices.length) {
    return callback({ partNumber: exactChoices[0].partNumber })
  }
  return callback({ partNumber: combinationsGroupedByType[uniqColors[index]][0].partNumber, changeSize: true })
}

const ColourAndSwatchPicker = ({
  combinationsGroupedByType: groupedByColors,
  groupedByFabrics,
  value: selectedColorName,
  selectedFabricName,
  partNumber,
  selectPartNumber,
  isFixed,
  size,
  swatchModalOpen,
  toggleSwatchOpen
}) => {
  const handleModalChange = value => {
    const payload = {
      swatchModalOpen: value
    }

    toggleSwatchOpen(payload)
  }

  const uniqColors = Object.keys(groupedByColors)
  const onSelect = event => selectPartNumber(event.partNumber, event.changeSize)()
  const selected = uniqColors.findIndex(color => color === selectedColorName)
  const selectVariant = buildSelector(groupedByColors, uniqColors, onSelect)

  const hasFabric = !!selectedFabricName
  const hasMoreThanMaxVariantColors = uniqColors.length > MAX_VARIATIONS
  const enableShowMore = hasFabric || hasMoreThanMaxVariantColors

  const mainSwatchProps = {
    colors: uniqColors,
    maxVariations: MAX_VARIATIONS,
    selectVariant,
    enableShowMore: hasMoreThanMaxVariantColors,
    selected,
    showMoreCallback: () => handleModalChange(true),
    isFixed,
    size
  }

  const currentPartNumberData =
    enableShowMore && groupedByColors[selectedColorName].find(attribute => attribute.partNumber === partNumber)

  return (
    <Styled.Wrapper>
      <Header colorName={selectedColorName} fabricName={selectedFabricName} />
      <ColorSwatchList {...mainSwatchProps} key='main-color-swatch' />
      {enableShowMore && (
        <>
          <Styled.OpenModalText onClick={() => handleModalChange(true)}>
            See range of fabrics and colours
          </Styled.OpenModalText>

          <Modal
            position='right'
            show={swatchModalOpen}
            onOpen={() => {
              handleModalChange(true)
            }}
            onClose={() => {
              handleModalChange(false)
            }}
          >
            <ModalHeader>Chose your fabric and colour</ModalHeader>
            <ModalContent>
              {hasFabric &&
                Object.keys(groupedByFabrics).map(fabricName => {
                  const groupedFabricColors = groupByTypeValue(groupedByFabrics[fabricName], 'Colour', 'value')
                  const colors = Object.keys(groupedFabricColors)

                  // Find the current partNumber and return the index of that partNumber for the "selected" prop
                  const selectedIndex =
                    selectedFabricName === fabricName && colors.findIndex(color => color === selectedColorName)

                  const modalSwatchProps = {
                    colors: Object.keys(groupedFabricColors),
                    maxVariations: 50,
                    selectVariant: buildSelector(groupedFabricColors, colors, onSelect),
                    selected: selectedIndex >= 0 ? selectedIndex : null,
                    isFixed,
                    size
                  }
                  const swatchTitle = config.title[fabricName] || fabricName
                  const swatchText = config.body[fabricName]
                  return (
                    <Styled.ModalSwatchContainer key={`${fabricName}`}>
                      <h5>
                        <strong>{swatchTitle}</strong>
                      </h5>
                      <p>{swatchText}</p>
                      <ColorSwatchList {...modalSwatchProps} key={`${fabricName}-color-swatch`} />
                    </Styled.ModalSwatchContainer>
                  )
                })}
              {!hasFabric && (
                <Styled.ModalSwatchContainer key='Colours'>
                  <h5>
                    <strong>{config.title.Colour}</strong>
                  </h5>
                  <p>{config.body.Colour}</p>
                  <ColorSwatchList
                    {...mainSwatchProps}
                    maxVariations={50}
                    enableShowMore={false}
                    key='Colour-color-swatch'
                  />
                </Styled.ModalSwatchContainer>
              )}
            </ModalContent>
            <ModalFooter>
              <Styled.ModalSwatchFooterContainer>
                <>
                  <strong>{`${selectedFabricName || ''} ${selectedColorName}`}</strong>
                  <strong>{`£${currentPartNumberData?.price}`}</strong>
                </>
              </Styled.ModalSwatchFooterContainer>
              <Button full onClick={() => handleModalChange(false)} tabIndex='0' data-test='view-product'>
                View in this colour
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}
    </Styled.Wrapper>
  )
}

export const mapStateToProps = state => {
  const { productStore = {} } = state
  const { meta = {} } = productStore
  const { swatchModalOpen } = meta

  const props = {
    swatchModalOpen
  }
  return props
}

const mapDispatchToProps = dispatch => {
  const toggleSwatchOpen = payload => dispatch(updateSwatchModalOpen(payload))

  const props = {
    toggleSwatchOpen
  }
  return props
}

ColourAndSwatchPicker.propTypes = {
  /**
   * a list of Variants grouped by Color
   */
  combinationsGroupedByType: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * a list of Variants grouped by Fabric
   */
  groupedByFabrics: PropTypes.arrayOf(PropTypes.object),
  /**
   * Specify selected color name if exits
   */
  value: PropTypes.string.isRequired,
  /**
   * Specify fabric name if exits
   */
  selectedFabricName: PropTypes.string,
  /**
   * Specify selected partNumber
   */
  partNumber: PropTypes.string,
  /**
   * Function to be callect when option choose
   */
  selectPartNumber: PropTypes.func.isRequired,
  /**
   * Optional prop passed into the swatch to hide it on small screens
   */
  isFixed: PropTypes.bool,
  /**
   * Optional prop passed into the swatch to change size of the circle (small, medium)
   */
  size: PropTypes.oneOf(['small', 'medium'])
}

ColourAndSwatchPicker.defaultProps = {
  isFixed: true,
  size: 'medium'
}
export default connect(mapStateToProps, mapDispatchToProps)(ColourAndSwatchPicker)

export { ColourAndSwatchPicker, buildSelector } // Used for tests
